
import { useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { useAppContext } from '../AppContext';

export const useRecurringTransactionDetector = () => {
  const { userId, db, appId } = useAppContext();
  const [potentialRecurring, setPotentialRecurring] = useState(null);

  useEffect(() => {
    if (!userId || !db) return;

    const transQuery = query(
      collection(db, `artifacts/${appId}/users/${userId}/transactions`),
      orderBy("timestamp", "desc")
    );

    const unsubscribe = onSnapshot(transQuery, (snapshot) => {
      const transactions = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      if (transactions.length < 2) {
        setPotentialRecurring(null);
        return;
      }
      
      // Simple detection logic for demonstration
      // In a real app, this would be much more robust
      const candidates = {};
      transactions.forEach(tx => {
        if (tx.amount < 0) { // Only consider spending
          const key = `${tx.description}-${Math.round(Math.abs(tx.amount))}`;
          if (!candidates[key]) {
            candidates[key] = [];
          }
          candidates[key].push(tx.timestamp.toDate());
        }
      });

      for (const key in candidates) {
        const dates = candidates[key];
        if (dates.length >= 2) {
          // Check if the last two transactions are roughly a month apart
          const lastDate = dates[0];
          const secondLastDate = dates[1];
          const diffTime = Math.abs(lastDate - secondLastDate);
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

          if (diffDays >= 28 && diffDays <= 32) {
            const [description] = key.split('-');
            setPotentialRecurring({ description });
            // For now, just find the first potential one and stop
            return;
          }
        }
      }
    });

    return () => unsubscribe();
  }, [userId, db, appId]);

  return { potentialRecurring, setPotentialRecurring };
};
